import React, { useMemo } from "react";
import { useTranslate } from "@refinedev/core";
import { VideoCompletionBreakdown, DocumentCompletionBreakdown } from "types/";
import { AnalyticsBarChart, type BarDatasetConfig } from "./AnalyticsBarChart";
import { analyticsTheme } from "./analyticsTheme";

const T_PREFIX =
  "media.projects.analytics.dashboard.summaryCards.completionByDevice";

type BreakdownKey = "by_time" | "by_device" | "by_source";

export interface ComparisonCardProps {
  title: string;
  videoCompletionData: VideoCompletionBreakdown | undefined;
  docCompletionData: DocumentCompletionBreakdown | undefined;
  breakdownKey: BreakdownKey;
  loading: boolean;
  /** Shown next to the title. */
  badge?: React.ReactNode;
}

export const ComparisonCard: React.FC<ComparisonCardProps> = ({
  title,
  videoCompletionData,
  docCompletionData,
  breakdownKey,
  loading,
  badge,
}) => {
  const t = useTranslate();
  const videoItems = videoCompletionData?.[breakdownKey] ?? [];
  const docItems = docCompletionData?.[breakdownKey] ?? [];

  const labels = useMemo(() => {
    const keySet = new Set<string>();
    videoItems.forEach((d) => keySet.add(d.breakdown_value));
    docItems.forEach((d) => keySet.add(d.breakdown_value));
    return Array.from(keySet);
  }, [videoItems, docItems]);

  const datasets = useMemo<BarDatasetConfig[]>(() => {
    const videoMap = new Map(
      videoItems.map((d) => [d.breakdown_value, d.avg_value ?? 0])
    );
    const docMap = new Map(
      docItems.map((d) => [d.breakdown_value, d.avg_reading_depth_pct ?? 0])
    );

    return [
      {
        label: t(`${T_PREFIX}.video`),
        data: labels.map((l) => Math.round((videoMap.get(l) ?? 0) * 100) / 100),
        color: analyticsTheme.datasetBlue,
      },
      {
        label: t(`${T_PREFIX}.document`),
        data: labels.map((l) => Math.round((docMap.get(l) ?? 0) * 100) / 100),
        color: analyticsTheme.datasetCyan,
      },
    ];
  }, [labels, videoItems, docItems, t]);

  return (
    <AnalyticsBarChart
      title={title}
      badge={badge}
      labels={labels}
      datasets={datasets}
      loading={loading}
      direction="horizontal"
      height={200}
    />
  );
};
